import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { L10nLocale, L10N_LOCALE } from 'angular-l10n';
import { debounceTime, distinctUntilChanged, filter, map, pairwise, tap } from 'rxjs/operators';
import { PagedList } from 'src/app/alert/models';
import { AdminsApi } from 'src/app/hickory-api';
import { ContactSelection } from '../model';
import { Filter, FilterField } from '../filtered-search/filtered-search.component';
import { BehaviorSubject, Subscription } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { AdminsTableColumns } from '../data-table/datasource/admins-datasource';
import { AuthService } from '../authentication/auth.service';

export interface SelectUsersData {
  selectedContacts: ContactSelection[],
  permissions?: string[];
  adminIds?: number[];
  externalIds?: string[];
  includeAssets?: boolean;
  includeChatId?: boolean;
  includePermissions?: boolean;
  isSingleSelect?: boolean;
  requiredIds?: number[];
  saveButtonLabel?: string;
  cancelButtonLabel?: string;
  title?: string;
  useV4Apis?: boolean;
  tenantId?: number;
}

@Component({
  selector: 'app-select-users',
  templateUrl: './select-users.component.html',
  styleUrls: ['./select-users.component.scss'],
  standalone: false
})
export class SelectUsersComponent implements OnInit {
  lastResponse: PagedList<ContactSelection>;
  loadUsersFailed: boolean = false;
  userSearchControl = new UntypedFormControl();
  private userSearch: string;
  private readonly pageSize = 10;
  private readonly sortOrder = 'lastName,firstName';

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  get selectedContacts() {
    return this.data?.selectedContacts || [];
  }

  get isSingleSelect(): boolean {
    return this.data?.isSingleSelect;
  }

  get saveButtonLabel(): string {
    return this.data?.saveButtonLabel || 'Save';
  }

  get cancelButtonLabel(): string {
    return this.data?.cancelButtonLabel || 'Cancel';
  }

  get title(): string {
    return this.data?.title;
  }

  get useV4Apis(): boolean {
    return this.data?.useV4Apis
  }
  filterByFields: FilterField[] = [];
  inheritedFilters: Filter[] = [];
  private readonly subscriptions = new Subscription();
  idsSearchTerm$ = new BehaviorSubject<number[]>(null);
  usernameSearchTerm$ = new BehaviorSubject<string>(null);
  emailSearchTerm$ = new BehaviorSubject<string>(null);
  groupSearchTerm$ = new BehaviorSubject<string>(null);
  roleSearchTerm$ = new BehaviorSubject<string>(null);
  tenantIdSearchTerm$ = new BehaviorSubject<number>(null);
  tenantNameSearchTerm$ = new BehaviorSubject<string>(null);
  stateSearchTerm$ = new BehaviorSubject<string>(null);
  credentialIdsSearchTerm$ = new BehaviorSubject<number[]>(null);
  externalIdsSearchTerm$ = new BehaviorSubject<string[]>(null);

  get tenantIdFilterValue(): number {
    if(this.authService.isSuperAdmin()) return this.tenantIdSearchTerm$.value;
    return this.authService.user.snapshot.tenantId;
  }

  constructor(
    private dialogRef: MatDialogRef<SelectUsersComponent>,
    @Inject(MAT_DIALOG_DATA) private data: SelectUsersData,
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private adminsApi: AdminsApi,
    private authService: AuthService
  ) {
    if (!this.data) {
      this.data = { selectedContacts: [] };
    }
  }

  ngOnInit(): void {
    // Debounce contact search and trigger the refresh
    this.userSearchControl.valueChanges.pipe(debounceTime(500)).subscribe(newValue => {
      this.userSearch = newValue;
      this.loadUsersPage();
    });

    this.setUpV4ApiFilteringAndSubscriptions();

    this.loadUsersPage();
  }

  loadUsersPage(page: number = 1) {
    if (this.useV4Apis) {
      this.loadUsersWithGetAdminUsers(page);
    } else {
      this.loadUsersWithGetAdmins(page);
    }
  }

  loadUsersWithGetAdmins(page: number) {
    this.loadUsersFailed = false;
    this.lastResponse = null;
    const request: any[] = [
      this.data.adminIds,
      this.data.externalIds,
      null,
      this.userSearch,
      this.data.permissions,
      'Any',
      this.data.includeAssets,
      this.data.includeChatId,
      this.data.includePermissions,
      page,
      this.pageSize,
      this.sortOrder
    ];
    this.adminsApi.getAdmins(...request).subscribe({
      next: r => {
        const users = ContactSelection.convertAdmins(this.selectedContacts, r);
        users.items.forEach(u => { if (this.data.requiredIds?.includes(u.adminId) === true) u.readonly = true; });
        this.lastResponse = users;
      },
      error: _ => this.loadUsersFailed = true
    });
  }

  loadUsersWithGetAdminUsers(page: number, name: string = null) {
    const request: any[] = [
      page,
      this.pageSize,
      this.sortOrder,
      this.getAdminIdsForV4Request(),
      name,
      this.usernameSearchTerm$.value,
      this.emailSearchTerm$.value,
      this.groupSearchTerm$.value,
      this.roleSearchTerm$.value,
      this.tenantIdFilterValue,
      this.tenantNameSearchTerm$.value,
      this.stateSearchTerm$.value,
      this.credentialIdsSearchTerm$.value,
      this.externalIdsSearchTerm$.value,
    ];
    this.loadUsersFailed = false;
    this.lastResponse = null;
    this.adminsApi.getAdminUsers(...request).subscribe({
      next: r => {
        const users = ContactSelection.convertAdminSummaries(this.selectedContacts, r);
        users.items.forEach(u => { if (this.data.requiredIds?.includes(u.adminId) === true) u.readonly = true; });
        this.lastResponse = users;
      },
      error: _ => this.loadUsersFailed = true
    })
  }

  getAdminIdsForV4Request() {
    const filteredIds = this.idsSearchTerm$.value;
    const requiredIds = this.data?.requiredIds || [];
    const merged = [...requiredIds, filteredIds];
    if(merged.length > 0) {
      return merged;
    }
    return null;
  }

  onUserSelected(user: ContactSelection, selected: boolean) {
    if (this.isSingleSelect) {
      this.data.selectedContacts = [];
      this.lastResponse?.items?.forEach(u => u.selected = false);
    }
    user.selected = selected;
    if (selected) {
      this.selectedContacts.push(user);
      if (this.isSingleSelect) {
        this.onSaveClick();
      }
    } else {
      const index = this.selectedContacts.findIndex(c => c.identifierEquals(user));
      if (index !== -1) {
        this.selectedContacts.splice(index, 1);
      }
    }
  }

  onUserPageChange(page: number) {
    this.loadUsersPage(page);
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  onSaveClick() {
    const data: SelectUsersData = { selectedContacts: this.selectedContacts, }
    this.dialogRef.close(data);
  }

  onSearchChange(value: string): void {
    this.loadUsersWithGetAdminUsers(this.getCurrentPage(), value);
  }

  onFilterChange(filter: Filter): void {
    const field = filter.field;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const value: any = filter.value;
    switch (field) {
      case 'username':
        this.usernameSearchTerm$.next(value);
        break;
      case 'email':
        this.emailSearchTerm$.next(value);
        break;
      case 'groups':
        this.groupSearchTerm$.next(value);
        break;
      case 'role':
        this.roleSearchTerm$.next(value);
        break;
      case 'tenantId':
        this.tenantIdSearchTerm$.next(value);
        break;
      case 'tenantName':
        this.tenantNameSearchTerm$.next(value);
        break;
      case 'state':
        this.stateSearchTerm$.next(value);
        break;
      case 'credentialsId':
        this.credentialIdsSearchTerm$.next([value]);
        break;
      default:
        break;
    }
  }

  getCurrentPage() {
    return this.paginator?.pageIndex + 1 || 1;
  }

  setUpV4ApiFilteringAndSubscriptions() {

    this.filterByFields = AdminsTableColumns.getAvailableFilterFields(this.authService, ['tenantId']);

    if (this.data?.adminIds?.length) {
      this.idsSearchTerm$.next(this.data.adminIds);
    }
    if (this.data?.externalIds?.length) {
      this.externalIdsSearchTerm$.next(this.data.externalIds);
    }
    if (this.data?.tenantId && this.authService.isSuperAdmin()) {
      this.tenantIdSearchTerm$.next(this.data.tenantId);
      this.inheritedFilters = [{ field: 'tenantId', value: this.data?.tenantId?.toString() }];
    }

    if(!this.authService.isSuperAdmin() && this.data) {
      this.data.tenantId = this.authService.user.snapshot.tenantId;
      this.tenantIdSearchTerm$.next(this.data.tenantId);
    }

    const subjects: BehaviorSubject<unknown>[] = [
      this.idsSearchTerm$,
      this.usernameSearchTerm$,
      this.emailSearchTerm$,
      this.groupSearchTerm$,
      this.roleSearchTerm$,
      this.tenantIdSearchTerm$,
      this.tenantNameSearchTerm$,
      this.stateSearchTerm$,
      this.credentialIdsSearchTerm$,
      this.externalIdsSearchTerm$,
    ];

    for (const subject of subjects) {
      this.subscriptions.add(subject
        .pipe(
          pairwise(),
          filter(([prev, curr]) => prev !== curr),
          map(([_, curr]) => curr),
          distinctUntilChanged(),
          tap((data: (object | unknown[]) & { length: number }) => {
            if (!!data || !!data?.length || !data?.length || !data) {
              this.loadUsersWithGetAdminUsers(this.getCurrentPage());
            }
          }),
        ).subscribe())
    }
  }
}
